// src/auth/AuthProvider.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiFetch, authGet, authSet, authClear, AUTH_CHANGED_EVENT } from "../api";

const AuthContext = createContext(null);

function toInt(x) {
  const n = Number(x);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function normalizeWalletIds(walletids) {
  const arr = Array.isArray(walletids) ? walletids : [];
  const out = [];
  for (const v of arr) {
    const id = toInt(v);
    if (id && id > 0) out.push(id);
  }
  // remove duplicados
  return Array.from(new Set(out));
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(() => authGet()); // { customerId, accessToken, refreshToken, walletids, defaultWalletId, email }

  useEffect(() => {
    function syncAuth() {
      setAuth(authGet());
    }

    function onStorage(e) {
      if (e.key === "finbot_auth") syncAuth();
    }

    window.addEventListener("storage", onStorage);
    window.addEventListener(AUTH_CHANGED_EVENT, syncAuth);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(AUTH_CHANGED_EVENT, syncAuth);
    };
  }, []);

  const isAuthed = !!auth?.accessToken && !!auth?.refreshToken;

  const requestPin = useCallback(async (email) => {
    await apiFetch("/api/auth/request-pin", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
  }, []);

  const verifyPin = useCallback(async (email, otp) => {
    const out = await apiFetch("/api/auth/verify-pin", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, otp }),
    });

    const customerId = toInt(out?.customerId);

    const walletids = normalizeWalletIds(out?.walletids); // 👈 mantém exatamente como backend manda
    const defaultWalletId = toInt(out?.defaultWalletId) || (walletids.length ? walletids[0] : null);

    const next = {
      customerId,
      walletids,
      defaultWalletId,
      accessToken: out?.accessToken,
      refreshToken: out?.refreshToken,
      email,
    };

    authSet(next);
    setAuth(next);
    return next;
  }, []);

  const logout = useCallback(() => {
    const rt = auth?.refreshToken;
    authClear();
    setAuth(null);

    if (rt) {
      apiFetch("/api/auth/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: rt }),
      }).catch(() => {});
    }
  }, [auth?.refreshToken]);

  const value = useMemo(
    () => ({ auth, isAuthed, requestPin, verifyPin, logout }),
    [auth, isAuthed, requestPin, verifyPin, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider />");
  return ctx;
}